<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';

import AgentCapacity from './AgentCapacity.vue';
const props = defineProps({
  agentLimit: { type: Number, default: null },
  usedSeats: { type: Number, default: 0 },
});
const emit = defineEmits(['close']);
const limitReached = computed(
  () => props.agentLimit !== null && props.usedSeats >= props.agentLimit
);
const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');
const selectedTeamIds = ref([]);

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const teams = useMapGetter('teams/getTeams');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const toggleTeam = teamId => {
  selectedTeamIds.value = selectedTeamIds.value.includes(teamId)
    ? selectedTeamIds.value.filter(id => id !== teamId)
    : [...selectedTeamIds.value, teamId];
};

onMounted(() => {
  store.dispatch('teams/get').catch(() => {
    useAlert(t('AGENT_MGMT.ADD.API.TEAMS_LOAD_ERROR'), {
      variant: 'warning',
    });
  });
});

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid || limitReached.value) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    const createdAgent = await store.dispatch('agents/create', payload);
    let teamAssignmentFailed = false;

    if (createdAgent?.id && selectedTeamIds.value.length) {
      try {
        await Promise.all(
          selectedTeamIds.value.map(teamId =>
            store.dispatch('teamMembers/create', {
              teamId,
              agentsList: [createdAgent.id],
            })
          )
        );
      } catch {
        teamAssignmentFailed = true;
      }
    }

    if (teamAssignmentFailed) {
      useAlert(t('AGENT_MGMT.ADD.API.TEAM_ERROR_MESSAGE'), {
        variant: 'warning',
      });
    } else {
      useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'), {
        variant: 'success',
      });
    }
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="agent-editor flex flex-col max-h-[85vh] overflow-y-auto">
    <div
      class="mx-8 mt-6 flex size-11 items-center justify-center rounded-2xl bg-n-brand/10 text-n-brand"
    >
      <span class="i-hugeicons-user-add-01 size-6" aria-hidden="true" />
    </div>
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <AgentCapacity
        class="mb-5 w-full"
        :limit="agentLimit"
        :used="usedSeats"
      />
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <fieldset class="mb-3 w-full">
        <legend class="mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.ADD.FORM.DEPARTMENTS.LABEL') }}
        </legend>
        <p class="mb-2 text-xs text-n-slate-10">
          {{ $t('AGENT_MGMT.ADD.FORM.DEPARTMENTS.HELP') }}
        </p>
        <div
          v-if="teams.length"
          class="grid max-h-40 gap-2 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-2"
        >
          <label
            v-for="team in teams"
            :key="team.id"
            class="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-n-slate-12 transition hover:bg-n-alpha-2"
          >
            <input
              type="checkbox"
              class="size-4 accent-n-brand"
              :checked="selectedTeamIds.includes(team.id)"
              @change="toggleTeam(team.id)"
            />
            <span class="i-hugeicons-user-group size-4 text-n-slate-9" />
            <span class="min-w-0 flex-1 truncate">{{ team.name }}</span>
          </label>
        </div>
        <div
          v-else
          class="rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-3 py-3 text-xs text-n-slate-10"
        >
          {{ $t('AGENT_MGMT.ADD.FORM.DEPARTMENTS.EMPTY') }}
        </div>
      </fieldset>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating || limitReached"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
.agent-editor :deep(form) {
  padding: 0 2rem 1.5rem;
}
.agent-editor :deep(input:not([type='checkbox'])),
.agent-editor :deep(select) {
  border-radius: 0.75rem;
}
@media (max-width: 480px) {
  .agent-editor :deep(form) {
    padding: 0 1rem 1rem;
  }
}
</style>
